<script setup lang="ts">
import type { FilterCategory, FilterDraft, FilterToggle } from '#shared/mobile-filter-drawer'
import { KsDrawer } from '@aschehoug/kloss'
import { faSlidersUp } from '@fortawesome/pro-light-svg-icons'

const props = withDefaults(defineProps<{
  categories: FilterCategory[]
  toggles?: FilterToggle[]
  initial: FilterDraft
  count: (draft: FilterDraft) => number
  minHeight?: number
}>(), {
  toggles: () => [],
  minHeight: 90,
})

const emit = defineEmits<{ commit: [draft: FilterDraft] }>()

const open = defineModel<boolean>('open', { default: false })

const headerRef = useTemplateRef<HTMLElement | undefined>('headerRef')
const bodyRef = useTemplateRef<HTMLElement | undefined>('bodyRef')

const view = ref<string>('main')
const draft = reactive<FilterDraft>({ categories: {}, toggles: {} })

const isSingleView = computed(() => props.categories.length === 1 && props.toggles.length === 0)
const primaryCategory = computed(() => props.categories[0]!)
const activeCategory = computed(() => props.categories.find(category => category.id === view.value))

const { slideDirection } = useDrawerSwipeBack(bodyRef, view, 'main')
const { heightStyle, isDragging } = useDrawerDrag(headerRef, open, { minHeight: props.minHeight })
useDrawerAutoClose(open)
useDrawerBodyLock(open)

const hasLocalFiltersChanged = computed(() =>
  props.categories.some(category => (draft.categories[category.id]?.length ?? 0) > 0)
  || props.toggles.some(toggle => draft.toggles[toggle.id]),
)

const hasSubViewSelection = computed(() =>
  view.value !== 'main' && (draft.categories[view.value]?.length ?? 0) > 0,
)

const localFilteredCount = computed(() => props.count(draft))

const transitionStyle = computed(() => {
  if (isDragging.value) {
    return 'none'
  }
  return isSingleView.value ? 'min-height 0.3s ease-out' : 'min-height 0.3s ease-out, transform 0.3s ease-out'
})

function seedDraft() {
  for (const category of props.categories) {
    draft.categories[category.id] = [...(props.initial.categories[category.id] ?? [])]
  }
  for (const toggle of props.toggles) {
    draft.toggles[toggle.id] = props.initial.toggles[toggle.id] ?? false
  }
}

function snapshot(): FilterDraft {
  const categories: FilterDraft['categories'] = {}
  for (const category of props.categories) {
    categories[category.id] = [...(draft.categories[category.id] ?? [])]
  }
  const toggles: FilterDraft['toggles'] = {}
  for (const toggle of props.toggles) {
    toggles[toggle.id] = draft.toggles[toggle.id] ?? false
  }
  return { categories, toggles }
}

function commitAndClose() {
  emit('commit', snapshot())
  open.value = false
}

function applyAndBackToMain() {
  emit('commit', snapshot())
  slideDirection.value = 'right'
  view.value = 'main'
}

function resetLocal() {
  for (const category of props.categories) {
    draft.categories[category.id] = []
  }
  for (const toggle of props.toggles) {
    draft.toggles[toggle.id] = false
  }
}

function openCategory(id: string) {
  slideDirection.value = 'left'
  view.value = id
}

function backToMain() {
  slideDirection.value = 'right'
  view.value = 'main'
}

watch(open, (isOpen) => {
  if (isOpen) {
    seedDraft()
  }
  else {
    view.value = 'main'
  }
})

// Seed immediately so the drawer renders correctly when mounted already-open (tests, SSR-open edge).
seedDraft()
</script>

<template>
  <Teleport to="body">
    <KsDrawer :open="open" size="100%" position="bottom" :dismissable="false" title="" @close="open = false">
      <template #header>
        <div ref="headerRef" class="cursor-grab mx-auto py-m px-l">
          <div class="bg-carbon-200 w-14 h-1 rounded-3xl" />
        </div>
      </template>
      <template #body>
        <template v-if="isSingleView">
          <div class="flex justify-end w-full px-s transition-height" :class="{ 'h-7': hasLocalFiltersChanged }">
            <button v-if="hasLocalFiltersChanged" class="text-small font-bold animate-fade" aria-label="Nullstill alle filtre" @click="resetLocal">
              Nullstill
            </button>
          </div>
          <DrawerFilterList
            :model-value="draft.categories[primaryCategory.id] ?? []"
            :title="primaryCategory.label"
            :icon="primaryCategory.icon"
            :options="primaryCategory.options"
            @update:model-value="value => draft.categories[primaryCategory.id] = value"
          />
          <Button variant="primary" class="w-full shrink-0" @click="commitAndClose">
            Vis {{ localFilteredCount }} treff
          </Button>
        </template>

        <div v-else ref="bodyRef" class="flex flex-col gap-m flex-1 min-h-0 relative">
          <Transition :name="slideDirection === 'left' ? 'slide-forward' : 'slide-back'">
            <div v-if="view === 'main'" key="main" class="flex flex-col gap-m">
              <div class="flex justify-end w-full px-s transition-height" :class="{ 'h-7': hasLocalFiltersChanged }">
                <button v-if="hasLocalFiltersChanged" class="text-small font-bold animate-fade" aria-label="Nullstill alle filtre" @click="resetLocal">
                  Nullstill
                </button>
              </div>
              <ul aria-label="Filtervalg" class="flex flex-col gap-3xs">
                <li v-for="category in categories" :key="category.id" class="border-b border-border-secondary">
                  <DrawerOptionButtonWithBadge
                    :icon="category.icon"
                    :label="category.label"
                    :badge-count="draft.categories[category.id]?.length ?? 0"
                    @click="openCategory(category.id)"
                  />
                </li>
                <li v-for="toggle in toggles" :key="toggle.id">
                  <FormCheckboxRight
                    :model-value="draft.toggles[toggle.id] ?? false"
                    :label="toggle.label"
                    :icon="toggle.icon"
                    @update:model-value="value => draft.toggles[toggle.id] = value"
                  />
                </li>
              </ul>
            </div>

            <DrawerFilterSubView
              v-else
              :key="view"
              :model-value="draft.categories[view] ?? []"
              :title="activeCategory?.label ?? ''"
              :icon="activeCategory?.icon ?? faSlidersUp"
              :options="activeCategory?.options ?? []"
              :reset-aria-label="activeCategory?.resetAriaLabel ?? ''"
              @update:model-value="value => draft.categories[view] = value"
              @back="backToMain"
            />
          </Transition>

          <Button v-if="view === 'main'" variant="primary" class="w-full shrink-0" @click="commitAndClose">
            Vis {{ localFilteredCount }} treff
          </Button>

          <Transition name="slide-up">
            <div v-if="view !== 'main' && hasSubViewSelection" class="w-full h-5xl -mb-xl flex justify-center items-center absolute bottom-0 bg-white">
              <Button variant="primary" class="w-full" @click="applyAndBackToMain">
                Bruk
              </Button>
            </div>
          </Transition>
        </div>
      </template>
    </KsDrawer>
  </Teleport>
</template>

<style scoped lang="postcss">
:deep(.ks-dialog) {
  min-height: v-bind(heightStyle);
  max-height: v-bind(heightStyle);
  transition: v-bind(transitionStyle);
  overscroll-behavior: contain;
}
</style>
